import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Calendar } from '../components/ui/calendar';
import { toast } from 'sonner';
import { formatDateUK } from '../lib/utils';
import { 
  Plus, 
  Calendar as CalendarIcon,
  Clock,
  MapPin,
  AlertCircle,
  Users,
  Edit2,
  Trash2,
  ChevronDown,
  Check,
  X as XIcon,
  Trophy,
  Medal,
  User
} from 'lucide-react';
import { format } from 'date-fns';
import { DrillTimer } from '../components/training';
import TrainingVoiceButton from '../components/training/TrainingVoiceButton';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ATTENDANCE_OPTIONS = [
  { value: 'available', label: 'Available', color: 'bg-emerald-100 text-emerald-700' },
  { value: 'unavailable', label: 'Unavailable', color: 'bg-red-100 text-red-700' },
  { value: 'injured', label: 'Injured', color: 'bg-amber-100 text-amber-700' },
  { value: 'working', label: 'Working', color: 'bg-indigo-100 text-indigo-700' },
  { value: 'sick', label: 'Sick', color: 'bg-pink-100 text-pink-700' },
  { value: 'excused', label: 'Excused', color: 'bg-gray-100 text-gray-700' }
];

export default function Training() {
  const { user, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [trainings, setTrainings] = useState([]);
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [attendanceDialogOpen, setAttendanceDialogOpen] = useState(false);
  const [editingTraining, setEditingTraining] = useState(null);
  const [selectedTraining, setSelectedTraining] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [displayMonth, setDisplayMonth] = useState(new Date());
  const [dragState, setDragState] = useState({
    isDragging: false,
    startX: 0,
    currentX: 0,
    deltaX: 0
  });
  const [formData, setFormData] = useState({
    date: format(new Date(), 'yyyy-MM-dd'),
    start_time: '18:00',
    end_time: '20:00',
    location: '',
    notes: ''
  });
  const [attendance, setAttendance] = useState([]);
  const [trainingLeaderboard, setTrainingLeaderboard] = useState([]);
  const [expandedPlayerId, setExpandedPlayerId] = useState(null);
  const [previousExpandedId, setPreviousExpandedId] = useState(null);
  const [voiceAiPlanHints, setVoiceAiPlanHints] = useState(null);
  const [generateAiPlan, setGenerateAiPlan] = useState(false);

  const handlePlayerClick = (playerId) => {
    if (expandedPlayerId === playerId) {
      // Collapsing current
      setPreviousExpandedId(playerId);
      setExpandedPlayerId(null);
    } else {
      // Expanding new one
      if (expandedPlayerId) {
        setPreviousExpandedId(expandedPlayerId);
      }
      setExpandedPlayerId(playerId);
    }
  };

  // Function to close dialog with animation
  const closeDialog = () => {
    setIsClosing(true);
    setTimeout(() => {
      setDialogOpen(false);
      setIsClosing(false);
    }, 300); // Match animation duration
  };

  // Prevent body scroll when slide-up panel is open
  useEffect(() => {
    if (dialogOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [dialogOpen]);

  useEffect(() => {
    if (user?.current_season_id) {
      fetchTrainings();
      fetchPlayers();
      fetchTrainingLeaderboard();
    } else {
      setLoading(false);
    }
  }, [user?.current_season_id]);

  const fetchTrainings = async () => {
    try {
      const response = await axios.get(`${API}/trainings`);
      setTrainings(response.data);
    } catch (error) {
      console.error('Failed to fetch trainings:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPlayers = async () => {
    try {
      const response = await axios.get(`${API}/players`);
      setPlayers(response.data);
    } catch (error) {
      console.error('Failed to fetch players:', error);
    }
  };

  const fetchTrainingLeaderboard = async () => {
    try {
      const response = await axios.get(`${API}/training-attendance`);
      setTrainingLeaderboard(response.data);
    } catch (error) {
      console.error('Failed to fetch training leaderboard:', error);
    }
  };

  const handleVoiceParse = (parsed) => {
    setFormData(prev => ({
      ...prev,
      date: parsed.date || prev.date,
      start_time: parsed.start_time || prev.start_time,
      end_time: parsed.end_time || prev.end_time,
      location: parsed.location ?? prev.location,
      notes: parsed.notes ?? prev.notes,
    }));
    if (parsed.wants_ai_plan && parsed.ai_plan_hints) {
      setVoiceAiPlanHints(parsed.ai_plan_hints);
      setGenerateAiPlan(true);
    } else {
      setVoiceAiPlanHints(null);
      setGenerateAiPlan(false);
    }
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    if (!formData.date || !formData.start_time) {
      toast.error('Please fill in required fields');
      return;
    }

    try {
      let trainingDoc = null;
      if (editingTraining) {
        const res = await axios.put(`${API}/trainings/${editingTraining.id}`, formData);
        trainingDoc = res.data;
        toast.success('Training updated successfully');
      } else {
        const res = await axios.post(`${API}/trainings`, formData);
        trainingDoc = res.data;
        toast.success('Training scheduled successfully');
      }

      // Optionally chain AI plan generation
      if (generateAiPlan && voiceAiPlanHints && !editingTraining && trainingDoc) {
        await generateAndLinkAiPlan(trainingDoc.id, voiceAiPlanHints);
      }

      fetchTrainings();
      closeDialog();
      setEditingTraining(null);
      setVoiceAiPlanHints(null);
      setGenerateAiPlan(false);
      setFormData({
        date: format(new Date(), 'yyyy-MM-dd'),
        start_time: '18:00',
        end_time: '20:00',
        location: '',
        notes: ''
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save training');
    }
  };

  const generateAndLinkAiPlan = async (trainingId, hints) => {
    try {
      // Sensible defaults for any missing hints (the AI plan endpoint requires all fields)
      const payload = {
        duration_min: hints.duration_min || 60,
        player_count: hints.player_count || 12,
        age_group: hints.age_group || 'U12',
        skill_level: hints.skill_level || 'intermediate',
        focus_area: hints.focus_area || 'passing',
        equipment_available: ['cones', 'bibs', 'balls'],
        surface: 'grass',
      };
      if (hints.secondary_focus) payload.secondary_focus = hints.secondary_focus;
      const planRes = await axios.post(`${API}/training/ai/generate`, payload);
      const plan = planRes.data;
      // Link plan into training.drills as a reference object
      await axios.put(`${API}/trainings/${trainingId}/drills`, [{
        type: 'ai_plan_ref',
        plan_id: plan.id,
        name: plan.name,
        focus_area: payload.focus_area,
      }]);
      toast.success('AI training plan generated and attached');
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail || 'Failed to generate AI plan';
      if (status === 412) {
        toast.error('Please accept the AI plan terms in /ai-plans first', { duration: 6000 });
      } else if (status === 429) {
        toast.error(detail, { duration: 6000 });
      } else {
        toast.error(detail);
      }
    }
  };

  const handleDelete = async (trainingId) => {
    if (!window.confirm('Are you sure you want to delete this training session?')) return;
    
    try {
      await axios.delete(`${API}/trainings/${trainingId}`);
      toast.success('Training deleted successfully');
      fetchTrainings();
    } catch (error) {
      toast.error('Failed to delete training');
    }
  };

  const handleEdit = (training) => {
    setEditingTraining(training);
    setFormData({
      date: training.date,
      start_time: training.start_time,
      end_time: training.end_time || '',
      location: training.location || '',
      notes: training.notes || ''
    });
    setDialogOpen(true);
  };

  const handleAttendance = (training) => {
    setSelectedTraining(training);
    const existingAttendance = training.attendance || [];
    const attendanceMap = players.map(player => {
      const existing = existingAttendance.find(a => a.player_id === player.id);
      return {
        player_id: player.id,
        status: existing?.status || 'available'
      };
    });
    setAttendance(attendanceMap);
    setAttendanceDialogOpen(true);
  };

  const handleSaveAttendance = async () => {
    try {
      await axios.put(`${API}/trainings/${selectedTraining.id}/attendance`, attendance);
      toast.success('Attendance saved successfully');
      fetchTrainings();
      fetchTrainingLeaderboard();
      setAttendanceDialogOpen(false);
    } catch (error) {
      toast.error('Failed to save attendance');
    }
  };

  const updateAttendance = (playerId, status) => {
    setAttendance(prev => prev.map(a => 
      a.player_id === playerId ? { ...a, status } : a
    ));
  };

  const resetForm = () => {
    setFormData({
      date: format(new Date(), 'yyyy-MM-dd'),
      start_time: '18:00',
      end_time: '20:00',
      location: '',
      notes: ''
    });
    setEditingTraining(null);
    setDialogOpen(false);
  };

  const getAttendanceOption = (status) => {
    return ATTENDANCE_OPTIONS.find(opt => opt.value === status) || ATTENDANCE_OPTIONS[0];
  };

  const getTrainingsForDate = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return trainings.filter(t => t.date === dateStr);
  };

  const trainingDates = trainings.map(t => new Date(t.date));

  // Swipe/Drag handlers for calendar navigation
  const swipeThreshold = 80; // Distance needed to trigger month change
  const sensitivity = 0.5; // How responsive the drag feels (lower = more sensitive)

  // Get the X position from either touch or mouse event
  const getEventX = (e) => {
    if (e.touches && e.touches.length > 0) {
      return e.touches[0].clientX;
    }
    return e.clientX;
  };

  const handleDragStart = (e) => {
    // Prevent text selection during drag
    if (e.type === 'mousedown') {
      e.preventDefault();
    }
    const x = getEventX(e);
    setDragState({
      isDragging: true,
      startX: x,
      currentX: x,
      deltaX: 0
    });
  };

  const handleDragMove = (e) => {
    if (!dragState.isDragging) return;
    
    const x = getEventX(e);
    const deltaX = x - dragState.startX;
    
    setDragState(prev => ({
      ...prev,
      currentX: x,
      deltaX: deltaX
    }));
  };

  const handleDragEnd = () => {
    if (!dragState.isDragging) return;
    
    const { deltaX } = dragState;
    
    // Check if swipe exceeded threshold
    if (Math.abs(deltaX) > swipeThreshold) {
      if (deltaX < 0) {
        // Dragged left = next month
        setDisplayMonth(prev => new Date(prev.getFullYear(), prev.getMonth() + 1, 1));
      } else {
        // Dragged right = previous month
        setDisplayMonth(prev => new Date(prev.getFullYear(), prev.getMonth() - 1, 1));
      }
    }
    
    // Reset drag state
    setDragState({
      isDragging: false,
      startX: 0,
      currentX: 0,
      deltaX: 0
    });
  };

  // Calculate visual transform during drag
  const getCalendarTransform = () => {
    if (!dragState.isDragging) return {};
    
    // Apply sensitivity and cap the visual movement
    const visualDelta = dragState.deltaX * sensitivity;
    const cappedDelta = Math.max(-100, Math.min(100, visualDelta));
    
    return {
      transform: `translateX(${cappedDelta}px)`,
      transition: 'none',
      opacity: 1 - Math.abs(cappedDelta) / 200 // Slight fade as you drag
    };
  };

  if (!user?.current_season_id) {
    return (
      <Layout>
        <div className="flex flex-col items-center justify-center min-h-[60vh] text-center" data-testid="no-season-message">
          <div className="w-20 h-20 rounded-2xl bg-amber-100 flex items-center justify-center mb-6">
            <AlertCircle className="w-10 h-10 text-amber-600" strokeWidth={1.5} />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-3 font-title">No Season Selected</h2>
          <p className="text-gray-500 mb-6">Create a season first to schedule training sessions.</p>
          <Button onClick={() => navigate('/seasons')} className="btn-primary rounded-xl">
            Go to Seasons
          </Button>
        </div>
      </Layout>
    );
  }

  const selectedDateTrainings = getTrainingsForDate(selectedDate);

  return (
    <Layout>
      <div className="space-y-6 page-slide-in" data-testid="training-container">
        {/* Header */}
        <div className="flex items-center justify-between gap-4">
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 font-title tracking-tight">Training</h1>
          {isAdmin && (
            <Button 
              variant="outline"
              size="icon"
              className="rounded-xl shrink-0 bg-gray-100 border-gray-200 text-gray-600 hover:bg-gray-200"
              onClick={() => {
                setEditingTraining(null);
                setFormData({
                  date: format(new Date(), 'yyyy-MM-dd'),
                  start_time: '18:00',
                  end_time: '20:00',
                  location: '',
                  notes: ''
                });
                setDialogOpen(true);
              }}
              data-testid="schedule-training-btn"
            >
              <Plus className="w-4 h-4" strokeWidth={1.5} />
            </Button>
          )}
        </div>

        {/* Slide-up Panel for Add/Edit Session */}
        {dialogOpen && (
          <>
            <div 
              className={`fixed inset-0 bg-black/50 z-50 transition-opacity duration-300 ${isClosing ? 'opacity-0' : 'opacity-100'}`}
              onClick={closeDialog}
            />
            <div 
              className={`fixed left-0 right-0 bottom-0 bg-white z-50 flex flex-col rounded-t-2xl shadow-2xl ${isClosing ? 'animate-slideDown' : 'animate-slideUp'}`}
              style={{ height: 'calc(100vh - 64px)' }}
            >
          <div className="p-6 border-b flex items-center justify-between">
            <div className="flex items-center gap-3">
              <h2 className="text-2xl font-bold font-title">
                {editingTraining ? 'Edit Training' : 'Schedule Training'}
              </h2>
              {!editingTraining && <TrainingVoiceButton onParse={handleVoiceParse} />}
            </div>
            <button
              onClick={closeDialog}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              data-testid="close-training-dialog-btn"
              aria-label="Close dialog"
            >
              <XIcon className="w-7 h-7" strokeWidth={2} />
            </button>
          </div>
          <div className="flex-1 overflow-y-auto p-6">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Date *</Label>
                <Input
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  className="rounded-xl"
                  data-testid="training-date-input"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Start Time *</Label>
                  <Input
                    type="time"
                    value={formData.start_time}
                    onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
                    className="rounded-xl"
                    data-testid="training-start-time-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>End Time</Label>
                  <Input
                    type="time"
                    value={formData.end_time}
                    onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
                    className="rounded-xl"
                    data-testid="training-end-time-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Location</Label>
                <Input
                  value={formData.location}
                  onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                  placeholder="Training ground, Stadium..."
                  className="rounded-xl"
                  data-testid="training-location-input"
                />
              </div>

              <div className="space-y-2">
                <Label>Notes</Label>
                <Input
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Focus areas, equipment needed..."
                  className="rounded-xl"
                  data-testid="training-notes-input"
                />
              </div>

              {voiceAiPlanHints && !editingTraining && (
                <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-3" data-testid="training-ai-plan-chip">
                  <label className="flex items-start gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={generateAiPlan}
                      onChange={(e) => setGenerateAiPlan(e.target.checked)}
                      className="mt-1"
                      data-testid="training-ai-plan-checkbox"
                    />
                    <div className="flex-1">
                      <p className="font-medium text-emerald-900 text-sm">Also generate an AI training plan</p>
                      <p className="text-xs text-emerald-700 mt-0.5">
                        {[
                          voiceAiPlanHints.focus_area && `Focus: ${voiceAiPlanHints.focus_area}`,
                          voiceAiPlanHints.age_group && voiceAiPlanHints.age_group,
                          voiceAiPlanHints.player_count && `${voiceAiPlanHints.player_count} players`,
                          voiceAiPlanHints.duration_min && `${voiceAiPlanHints.duration_min} min`,
                        ].filter(Boolean).join(' · ')}
                      </p>
                      <p className="text-xs text-emerald-600 mt-0.5">Uses 1 of your monthly AI generations.</p>
                    </div>
                  </label>
                </div>
              )}
            </div>
          </div>
          <div className="p-6 pb-20 border-t bg-white">
            <div className="flex gap-3">
              <Button 
                type="button" 
                variant="outline" 
                onClick={closeDialog} 
                className="flex-1 rounded-xl"
              >
                Cancel
              </Button>
              <Button 
                type="button" 
                onClick={handleSubmit}
                className="flex-1 bg-[#064E3B] hover:bg-[#065f46] text-white rounded-xl" 
                data-testid="save-training-btn"
              >
                {editingTraining ? 'Update' : 'Schedule'}
              </Button>
            </div>
          </div>
        </div>
        </>
        )}

        {/* Calendar and Sessions */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
          {/* Calendar */}
          <Card className="lg:col-span-1" data-testid="training-calendar">
            <CardHeader>
              <CardTitle className="text-xl font-semibold font-title">Calendar</CardTitle>
            </CardHeader>
            <CardContent>
              <div
                onTouchStart={handleDragStart}
                onTouchMove={handleDragMove}
                onTouchEnd={handleDragEnd}
                onMouseDown={handleDragStart}
                onMouseMove={handleDragMove}
                onMouseUp={handleDragEnd}
                onMouseLeave={handleDragEnd}
                className="select-none cursor-grab active:cursor-grabbing overflow-hidden relative"
                style={{ touchAction: 'pan-y' }}
              >
                <div 
                  style={{
                    ...getCalendarTransform(),
                    transition: dragState.isDragging ? 'none' : 'transform 0.3s ease-out, opacity 0.3s ease-out'
                  }}
                >
                  <Calendar
                    mode="single"
                    selected={selectedDate}
                    onSelect={(date) => date && setSelectedDate(date)}
                    month={displayMonth}
                    onMonthChange={setDisplayMonth}
                    className="rounded-xl"
                    modifiers={{
                      training: trainingDates
                    }}
                    modifiersStyles={{
                      training: { backgroundColor: '#d1fae5', color: '#065f46', fontWeight: 'bold' }
                    }}
                  />
                </div>
                {/* Swipe indicator */}
                {dragState.isDragging && Math.abs(dragState.deltaX) > 20 && (
                  <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
                    <div className={`text-xs font-medium px-3 py-1 rounded-full bg-gray-900/70 text-white ${
                      dragState.deltaX < 0 ? 'translate-x-8' : '-translate-x-8'
                    }`}>
                      {dragState.deltaX < 0 ? 'Next →' : '← Prev'}
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Sessions for Selected Date */}
          <Card className="lg:col-span-2" data-testid="training-sessions">
            <CardHeader>
              <CardTitle className="text-xl font-semibold font-title">
                Sessions on {format(selectedDate, 'MMMM d, yyyy')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {selectedDateTrainings.length === 0 ? (
                <div className="text-center py-12 text-gray-400">
                  <CalendarIcon className="w-12 h-12 mx-auto mb-3 opacity-50" strokeWidth={1.5} />
                  <p>No training sessions scheduled</p>
                  {isAdmin && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="mt-4"
                      onClick={() => {
                        setFormData({ ...formData, date: format(selectedDate, 'yyyy-MM-dd') });
                        setDialogOpen(true);
                      }}
                    >
                      <Plus className="w-4 h-4 mr-1" strokeWidth={1.5} />
                      Add Session
                    </Button>
                  )}
                </div>
              ) : (
                <div className="flex flex-col gap-4">
                  {selectedDateTrainings.map((training) => (
                    <div key={training.id} className="p-3 sm:p-4 bg-gray-50 rounded-xl" data-testid={`training-card-${training.id}`}>
                      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-3">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 text-gray-900">
                            <Clock className="w-4 h-4 text-gray-400 flex-shrink-0" strokeWidth={1.5} />
                            <span className="font-medium">{training.start_time || '—'}</span>
                            {training.end_time && (
                              <>
                                <span className="text-gray-400">-</span>
                                <span className="font-medium">{training.end_time}</span>
                              </>
                            )}
                          </div>
                          {training.location && (
                            <div className="flex items-center gap-2 text-sm text-gray-500 mt-1">
                              <MapPin className="w-4 h-4 flex-shrink-0" strokeWidth={1.5} />
                              <span className="truncate">{training.location}</span>
                            </div>
                          )}
                          {training.notes && (
                            <p className="text-sm text-gray-500 mt-2 line-clamp-2">{training.notes}</p>
                          )}
                          {Array.isArray(training.drills) && training.drills.some(d => d?.type === 'ai_plan_ref') && (() => {
                            const ref = training.drills.find(d => d?.type === 'ai_plan_ref');
                            return (
                              <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); navigate('/ai-plans'); }}
                                className="mt-2 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs font-medium hover:bg-emerald-100 transition-colors"
                                data-testid={`training-ai-plan-link-${training.id}`}
                              >
                                AI plan: {ref.name?.slice(0, 40) || 'View plan'}
                              </button>
                            );
                          })()}
                        </div>
                        <div className="flex gap-2 flex-shrink-0">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleAttendance(training)}
                            className="rounded-lg text-xs sm:text-sm"
                            data-testid={`attendance-btn-${training.id}`}
                          >
                            <Users className="w-4 h-4 sm:mr-1" strokeWidth={1.5} />
                            <span className="hidden sm:inline">Attendance</span>
                          </Button>
                          {isAdmin && (
                            <>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleEdit(training)}
                                className="rounded-lg"
                                data-testid={`edit-training-${training.id}`}
                              >
                                <Edit2 className="w-4 h-4" strokeWidth={1.5} />
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleDelete(training.id)}
                                className="text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg"
                                data-testid={`delete-training-${training.id}`}
                              >
                                <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                              </Button>
                            </>
                          )}
                        </div>
                      </div>

                      {/* Attendance Summary */}
                      {training.attendance?.length > 0 && (
                        <div className="flex flex-wrap gap-2 pt-3 border-t border-gray-200">
                          {ATTENDANCE_OPTIONS.map(opt => {
                            const count = training.attendance.filter(a => a.status === opt.value).length;
                            if (count === 0) return null;
                            return (
                              <span key={opt.value} className={`text-xs px-2 py-1 rounded-full ${opt.color}`}>
                                {opt.label}: {count}
                              </span>
                            );
                          })}
                        </div>
                      )}

                      {/* Drill Timer */}
                      <DrillTimer
                        training={training}
                        presets={user?.drill_presets}
                        onDrillsUpdate={(drills) => {
                          setTrainings(prev => prev.map(t =>
                            t.id === training.id ? { ...t, drills } : t
                          ));
                        }}
                      />
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* All Trainings */}
        <Card data-testid="all-trainings">
          <CardHeader>
            <CardTitle className="text-xl font-semibold font-title">All Scheduled Sessions</CardTitle>
          </CardHeader>
          <CardContent>
            {trainings.length === 0 ? (
              <div className="text-center py-8 text-gray-400">
                <p>No training sessions scheduled yet</p>
              </div>
            ) : (
              <>
                {/* Mobile Card View */}
                <div className="block sm:hidden space-y-3">
                  {trainings.sort((a, b) => new Date(a.date) - new Date(b.date)).map((training) => (
                    <div key={training.id} className="p-3 bg-gray-50 rounded-xl">
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <p className="font-medium text-gray-900">{formatDateUK(training.date)}</p>
                          <p className="text-sm text-gray-600">{training.start_time}{training.end_time && ` - ${training.end_time}`}</p>
                        </div>
                        <div className="flex gap-1">
                          <Button variant="ghost" size="sm" onClick={() => handleAttendance(training)} className="h-8 w-8 p-0">
                            <Users className="w-4 h-4" strokeWidth={1.5} />
                          </Button>
                          {isAdmin && (
                            <>
                              <Button variant="ghost" size="sm" onClick={() => handleEdit(training)} className="h-8 w-8 p-0">
                                <Edit2 className="w-4 h-4" strokeWidth={1.5} />
                              </Button>
                              <Button variant="ghost" size="sm" onClick={() => handleDelete(training.id)} className="text-red-600 h-8 w-8 p-0">
                                <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                              </Button>
                            </>
                          )}
                        </div>
                      </div>
                      {training.location && (
                        <p className="text-sm text-gray-500 flex items-center gap-1">
                          <MapPin className="w-3 h-3" strokeWidth={1.5} />
                          {training.location}
                        </p>
                      )}
                      {training.attendance?.length > 0 && (
                        <p className="text-xs text-gray-500 mt-2">
                          {training.attendance.filter(a => a.status === 'available').length} / {training.attendance.length} available
                        </p>
                      )}
                    </div>
                  ))}
                </div>

                {/* Desktop Table View */}
                <div className="hidden sm:block overflow-x-auto">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Time</th>
                        <th>Location</th>
                        <th>Attendance</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {trainings.sort((a, b) => new Date(a.date) - new Date(b.date)).map((training) => (
                        <tr key={training.id}>
                          <td>{formatDateUK(training.date)}</td>
                          <td>{training.start_time}{training.end_time && ` - ${training.end_time}`}</td>
                          <td>{training.location || '-'}</td>
                          <td>
                            {training.attendance?.length > 0 ? (
                              <span className="text-sm text-gray-500">
                                {training.attendance.filter(a => a.status === 'available').length} / {training.attendance.length} available
                              </span>
                            ) : (
                              <span className="text-sm text-gray-400">Not recorded</span>
                            )}
                          </td>
                          <td>
                            <div className="flex gap-2">
                              <Button variant="ghost" size="sm" onClick={() => handleAttendance(training)}>
                                <Users className="w-4 h-4" strokeWidth={1.5} />
                              </Button>
                              {isAdmin && (
                                <>
                                  <Button variant="ghost" size="sm" onClick={() => handleEdit(training)}>
                                    <Edit2 className="w-4 h-4" strokeWidth={1.5} />
                                  </Button>
                                  <Button variant="ghost" size="sm" onClick={() => handleDelete(training.id)} className="text-red-600">
                                    <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                                  </Button>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Training Attendance Leaderboard */}
        <Card data-testid="training-leaderboard">
          <CardHeader>
            <CardTitle className="text-xl font-semibold font-title flex items-center gap-2">
              <Trophy className="w-5 h-5 text-emerald-600" strokeWidth={1.5} />
              <span className="hidden sm:inline">Training Attendance Leaderboard</span>
              <span className="sm:hidden">Attendance Leaderboard</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {trainingLeaderboard.length === 0 ? (
              <div className="text-center py-8 text-gray-400">
                <Medal className="w-12 h-12 mx-auto mb-3 opacity-50" strokeWidth={1.5} />
                <p>No attendance data recorded yet</p>
                <p className="text-sm mt-1">Record attendance for training sessions to see the leaderboard</p>
              </div>
            ) : (
              <div className="flex flex-col gap-4">
                {trainingLeaderboard.map((player) => {
                  const isExpanded = expandedPlayerId === player.player_id;
                  const wasExpanded = previousExpandedId === player.player_id && !isExpanded;
                  
                  return (
                  <div 
                    key={player.player_id} 
                    className="bg-gray-50 rounded-xl overflow-hidden cursor-pointer hover:bg-gray-100"
                    onClick={() => handlePlayerClick(player.player_id)}
                    data-testid={`training-leaderboard-row-${player.player_id}`}
                  >
                    {/* Main Row */}
                    <div className="flex items-center justify-between p-3 gap-3">
                      <div className="flex items-center gap-2 sm:gap-4 min-w-0">
                        {/* Rank Badge */}
                        <div className={`w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center text-xs sm:text-sm font-bold flex-shrink-0 ${
                          player.rank === 1 ? 'bg-gradient-to-br from-amber-400 to-amber-600 text-white' :
                          player.rank === 2 ? 'bg-gray-400 text-white' :
                          player.rank === 3 ? 'bg-gradient-to-br from-orange-400 to-orange-600 text-white' :
                          'bg-gray-200 text-gray-600'
                        }`}>
                          {player.rank}
                        </div>
                        
                        {/* Player Name */}
                        <p className="font-medium text-gray-900 text-sm sm:text-base truncate">{player.name}</p>
                      </div>
                      
                      {/* Attendance Stats */}
                      <div className="text-right flex-shrink-0 flex items-center gap-2">
                        <div>
                          <p className="text-xl sm:text-2xl font-bold text-emerald-600">{player.attended}</p>
                          <p className="text-xs text-gray-500">
                            of {player.total_sessions}
                          </p>
                        </div>
                        <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform duration-300 ${isExpanded ? 'rotate-180' : ''}`} strokeWidth={1.5} />
                      </div>
                    </div>
                    
                    {/* Expanded Details - Inside Grey Container */}
                    <div className={`leaderboard-expand ${isExpanded ? 'expanded' : ''} ${wasExpanded ? 'collapsed' : ''}`}>
                      {player.status_breakdown && (
                          <div className="px-3 pb-3 pt-1">
                            <div className="border-t border-gray-200 pt-3">
                              <p className="text-xs text-gray-500 mb-2 font-medium uppercase tracking-wide">Attendance Breakdown</p>
                              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                                {player.status_breakdown.available > 0 && (
                                  <div className="flex items-center gap-2 px-3 py-2 bg-white rounded-lg shadow-sm">
                                    <div className="w-2 h-2 rounded-full bg-emerald-500"></div>
                                    <span className="text-sm text-gray-700">Available</span>
                                    <span className="text-sm font-semibold text-emerald-600 ml-auto">{player.status_breakdown.available}</span>
                                  </div>
                                )}
                                {player.status_breakdown.unavailable > 0 && (
                                  <div className="flex items-center gap-2 px-3 py-2 bg-white rounded-lg shadow-sm">
                                    <div className="w-2 h-2 rounded-full bg-red-500"></div>
                                    <span className="text-sm text-gray-700">Unavailable</span>
                                    <span className="text-sm font-semibold text-red-600 ml-auto">{player.status_breakdown.unavailable}</span>
                                  </div>
                                )}
                                {player.status_breakdown.injured > 0 && (
                                  <div className="flex items-center gap-2 px-3 py-2 bg-white rounded-lg shadow-sm">
                                    <div className="w-2 h-2 rounded-full bg-orange-500"></div>
                                    <span className="text-sm text-gray-700">Injured</span>
                                    <span className="text-sm font-semibold text-orange-600 ml-auto">{player.status_breakdown.injured}</span>
                                  </div>
                                )}
                                {player.status_breakdown.working > 0 && (
                                  <div className="flex items-center gap-2 px-3 py-2 bg-white rounded-lg shadow-sm">
                                    <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                                    <span className="text-sm text-gray-700">Working</span>
                                    <span className="text-sm font-semibold text-blue-600 ml-auto">{player.status_breakdown.working}</span>
                                  </div>
                                )}
                                {player.status_breakdown.sick > 0 && (
                                  <div className="flex items-center gap-2 px-3 py-2 bg-white rounded-lg shadow-sm">
                                    <div className="w-2 h-2 rounded-full bg-purple-500"></div>
                                    <span className="text-sm text-gray-700">Sick</span>
                                    <span className="text-sm font-semibold text-purple-600 ml-auto">{player.status_breakdown.sick}</span>
                                  </div>
                                )}
                                {player.status_breakdown.excused > 0 && (
                                  <div className="flex items-center gap-2 px-3 py-2 bg-white rounded-lg shadow-sm">
                                    <div className="w-2 h-2 rounded-full bg-gray-500"></div>
                                    <span className="text-sm text-gray-700">Excused</span>
                                    <span className="text-sm font-semibold text-gray-600 ml-auto">{player.status_breakdown.excused}</span>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        )}
                    </div>
                  </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Attendance Dialog */}
        <Dialog open={attendanceDialogOpen} onOpenChange={setAttendanceDialogOpen}>
          <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-2xl font-bold font-title">
                {isAdmin ? 'Record Attendance' : 'View Attendance'}
              </DialogTitle>
              <DialogDescription className="sr-only">
                {isAdmin ? 'Record player attendance for this training session' : 'View player attendance for this training session'}
              </DialogDescription>
            </DialogHeader>
            <div className="mt-4 flex flex-col gap-4">
              {attendance.map((record) => {
                const player = players.find(p => p.id === record.player_id);
                if (!player) return null;
                const currentOption = getAttendanceOption(record.status);
                return (
                  <div key={record.player_id} className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                    <div className="flex items-center gap-2">
                      <span className="font-bold text-gray-700">#{player.jersey_number}</span>
                      <span className="font-medium">{player.name}</span>
                    </div>
                    {isAdmin ? (
                      <Select
                        value={record.status}
                        onValueChange={(value) => updateAttendance(record.player_id, value)}
                      >
                        <SelectTrigger className={`w-36 rounded-lg ${currentOption.color}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {ATTENDANCE_OPTIONS.map(opt => (
                            <SelectItem key={opt.value} value={opt.value}>
                              {opt.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <span className={`px-3 py-1 rounded-lg text-sm font-medium ${currentOption.color}`}>
                        {currentOption.label}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
            <div className="flex gap-3 pt-4">
              <Button variant="outline" onClick={() => setAttendanceDialogOpen(false)} className="flex-1 rounded-xl">
                {isAdmin ? 'Cancel' : 'Close'}
              </Button>
              {isAdmin && (
                <Button onClick={handleSaveAttendance} className="flex-1 bg-[#064E3B] hover:bg-[#065f46] text-white rounded-xl" data-testid="save-attendance-btn">
                  Save Attendance
                </Button>
              )}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
